/** The engine run the page is currently showing, for the Nucleus dock.
 *
 *  Sent with every turn as `uiContext.active_run`. Core lists the runs it
 *  recorded, but which run the planner is *looking at* is a selection living
 *  in the browser, so unless the turn carries it, "which run am I on?" can
 *  only be answered with a guess — and a confident wrong run is worse than
 *  none.
 *
 *  Read from the live selection at send time, never threaded down as a prop:
 *  a prop chain goes stale exactly when the planner changes the selector
 *  mid-conversation, which is the case this exists for. The dock is mounted
 *  inside `RunSelectionProvider`, so it subscribes to the same selection the
 *  selector writes and cannot disagree with what is on screen.
 *
 *  Kept deliberately small. Core cleans and caps `ui_context` before it
 *  reaches the prompt, so this carries what identifies the run and nothing
 *  else; anything more about it is a tool call away.
 */

/** Routes whose view mounts the run selector.
 *
 *  Listed explicitly, the same way `nucleusActiveReport` lists its report
 *  hosts, and for the same reason: the dock cannot see which view is mounted,
 *  only the path. Keep in step with routes.tsx — a page missing here still
 *  sends its run, flagged as the app default rather than a page selection,
 *  which is the safe direction to be wrong in.
 *
 *  The provider resolves a run app-wide (most recent when nothing is pinned),
 *  so a page with no selector still HAS one; it is just not something the
 *  planner chose while looking at that page. */
const RUN_SELECTOR_PATHS = ["/replenishment", "/workflows"] as const;

/** Does the page at this path show a run selector the planner could have used? */
export const pageShowsRunSelector = (pathname: string): boolean =>
  RUN_SELECTOR_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export interface ActiveRun {
  /** Core's module-scoped run number for the Replenishment module, as a
   *  string — the same number the engine console, the run history and
   *  `allocationRuns` use, so the model can repeat it back to a planner and a
   *  tool can look it up. */
  run_id: string;
  /** Exactly the string the Data selector shows, so the model's answer and
   *  the planner's screen use the same words. */
  label: string;
  /** Which registry this run came from: core's allocation run ledger. (Until
   *  2026-09-10 the selector listed LMNT's own V4 planning runs, a different
   *  store with different numbers; that stack is retired.) */
  source: "core_allocation_ledger";
  /** What KIND of run this is. The selector lists the allocation ENGINE's
   *  runs, so the number here is an engine run number. */
  kind: "engine_run";
  /** When the run started, so "is this the latest?" has a footing. */
  as_of: string | null;
  /** True when a run selector is actually on screen and this is what it shows.
   *
   *  False means the app resolved a default (the provider falls back to the
   *  most recent run app-wide) on a page that shows no selector, so the model
   *  must not describe it as "the run this page is on". */
  selected_on_page: boolean;
}

/** The selected run as the dock should report it, or null when the page has
 *  no selection to report — no runs, still loading, or a page that has no run
 *  selector at all. Null is omitted from `ui_context` rather than sent as an
 *  empty shape, so the model is never handed a run-shaped blank to reason from. */
export function activeRunFromSelection(
  selection: {
    run: { id?: string | null; createdAt?: string | null } | null;
    label: string | null;
    /** Whether the page the turn came from actually shows a run selector. */
    selectedOnPage?: boolean;
  } | null,
): ActiveRun | null {
  const run = selection?.run;
  const runId = run?.id?.trim();
  // No usable identifier is the same as no run: a label alone would let the
  // model name a run nothing else could look up.
  if (!runId) return null;
  const label = selection?.label?.trim();
  if (!label) return null;
  return {
    run_id: runId,
    label,
    source: "core_allocation_ledger",
    kind: "engine_run",
    as_of: run?.createdAt ?? null,
    selected_on_page: selection?.selectedOnPage ?? false,
  };
}
